<template lang="pug">
    include ../../media/pug/mixins
    +b.document-view-img.document-view__img(
        v-bind:class="documentClass"
        data-dragscroll
    )
        +e.container(v-bind:style="imgStyles")
            +e.overlay(
                v-if="isOverlayVisible"
            )
            rsl-loader(
                v-if="imgIsLoading"
                viewType="book"
                iconClass="preloader-icon__book"
                v-bind:style="`transform: rotate(-${$route.query.rotate}deg)`"
            )
            img(
                data-dragscroll
                v-else
                v-bind:src="imgUrl"
            )

</template>


<script>
import { mapGetters } from 'vuex';
import RslLoader from '@RSL/RslLoader/RslLoader';
import documentView from '@/helpers/mixins/document-view';
import api from '@/helpers/api';
import getBufferFrom from '@/helpers/bufer';

export default {
    components: {
        RslLoader
    },
    mixins: [documentView],
    props: {
        isAllMarksVisible: {
            type: Boolean,
            default: false
        },
        isOverlayVisible: {
            type: Boolean,
            default: false
        },
        viewerHeight: {
            type: Number,
            required: true,
            default: 0
        },
        viewerWidth: {
            type: Number,
            required: true,
            default: 0
        }
    },
    data() {
        return {
            imgIsLoading: true,
            cancelFetchImage: null,
            imgUrl: '',
            container: null,
            startPoint: {
                x: 0,
                y: 0
            },
            endPoint: {
                x: 0,
                y: 0
            },
            markSize: {
                width: 36,
                height: 46
            },
            origin: {
                x: 50,
                y: 50
            },
            crop: {
                isVisible: false,
                height: 0,
                width: 0
            }
        };
    },
    computed: {
        ...mapGetters([
            'getMainImageParams',
            'isMobile',
            'isDesktop',
            'getMainImgZoomWidth',
            'getMainImgZoomHeight',
            'getZoom',
            'isAddMarkMode',
            'isMainImgLandscape',
            'getActiveMark',
            'getActiveArea',
            'isMarkEditorVisible',
            'isMarkConfirmVisible',
            'isMarkConfirmed',
            'isMarkEditMode',
            'getMarks',
            'getAreas',
            'isMenuPanelVisible',
            'isModalVisible'
        ]),
        isVerticalCenter() {
            return this.getMainImgZoomHeight <= this.viewerHeight;
        },
        isMobileLandscape() {
            return this.isMobile && this.isMainImgLandscape;
        },
        isHorzCenter() {
            if (this.isMainImgLandscape) {
                if (this.isVerticalCenter || this.isMobile) {
                    return true;
                }
                return false;
            }
            return this.getMainImgZoomWidth < this.viewerWidth;
        },
        documentClass() {
            return [
                { 'document-view-img_vertical-center': this.isVerticalCenter },
                { 'document-view-img_horz-center': this.isHorzCenter },
                { 'document-view-img_is-bg-visible': this.isOverlayVisible }
            ];
        },
        imgStyles() {
            return {
                width: `${this.getMainImgZoomWidth}px`,
                height: `${this.getMainImgZoomHeight}px`,
                transform: `rotate(${this.$route.query.rotate}deg)`,
                transformOrigin: this.isMobileLandscape ? '50% 50%' : `${this.origin.x}% ${this.origin.y}%`
            };
        },
        markCurrentSize() {
            return {
                width: this.getZoom * (this.markSize.width * (this.isMobile ? 3 : 1)),
                height: this.getZoom * (this.markSize.height * (this.isMobile ? 3 : 1))
            };
        },
        cropStartPosition() {
            return {
                x: Math.min(this.startPosition().x, this.endPosition().x),
                y: Math.min(this.startPosition().y, this.endPosition().y)
            };
        },
        cropParams() {
            const { x, y } = this.cropStartPosition;
            const { width, height } = this.crop;
            return {
                startX: x / this.getZoom,
                startY: y / this.getZoom,
                endX: (x + width) / this.getZoom,
                endY: (y + height) / this.getZoom
            };
        }
    },
    watch: {
        $route(to, from) {
            const { rotate } = to.query;
            if (from.query.rotate !== rotate) {
                this.setRotateOrigin(rotate);
                this.containerSizeMath();
            } else {
                this.setImage(to).then(() => {
                    this.setRotateOrigin(rotate);
                    this.containerSizeMath();
                    this.imgIsLoading = false;
                });
            }
        },
        getZoom() {
            this.setRotateOrigin(+this.$route.query.rotate);
        }
    },
    created() {
        this.setImage(this.$route).then(() => {
            if (this.isDesktop) {
                this.$documentView.setFullHeight();
            } else {
                this.containerSizeMath();
            }
            this.setRotateOrigin(this.$route.query.rotate);
            this.imgIsLoading = false;
        });
    },
    mounted() {
        this.container = document.querySelector('.document-view-img__container');
    },
    methods: {
        getBounds() {
            return this.container.getBoundingClientRect();
        },
        calcPositionWithRotate({ x, y }) {
            const {
                top, right, left, bottom
            } = this.getBounds();
            switch (+this.$route.query.rotate) {
            case 90:
            case -270:
                return {
                    x: y - top,
                    y: right - x
                };
            case 180:
            case -180:
                return {
                    x: right - x,
                    y: bottom - y
                };
            case 270:
            case -90:
                return {
                    x: bottom - y,
                    y: x - left
                };
            default:
                return {
                    x: x - left,
                    y: y - top
                };
            }
        },
        startPosition() {
            return this.calcPositionWithRotate(this.startPoint);
        },
        endPosition() {
            const { x, y } = this.calcPositionWithRotate(this.endPoint);
            return {
                x: x <= 0 ? 0 : x,
                y: y <= 0 ? 0 : y
            };
        },
        onMouseDown(e) {
            if (e.target.closest('.document-view-img__container')) {
                this.setPoint(e, 'startPoint');
                if (!this.isOverlayVisible) {
                    this.$emit('update:isOverlayVisible', true);
                }
                this.toggleEvents(true);
            }
        },
        onMouseUp(e) {
            this.setPoint(e, 'endPoint');
            this.toggleEvents(false);
        },
        onMouseMove(e) {
            e.stopPropagation();
            if (e.x !== this.startPoint.x || e.y !== this.startPoint.y) {
                this.crop.isVisible = true;
                this.setPoint(e, 'endPoint');
                this.setCropBounds();
            } else {
                this.crop.isVisible = false;
            }
        },
        toggleHotKeys(val) {
            if (val) {
                if ('activeElement' in document) { document.activeElement.blur(); }
                document.addEventListener('keydown', this.checkHotKeys);
            } else {
                document.removeEventListener('keydown', this.checkHotKeys);
            }
        },
        checkHotKeys(e) {
            if (e.key === 'Escape') {
                document.removeEventListener('keydown', this.checkHotKeys);
                this.setMarkIsConfirmed(false);
            }
            if (e.key === 'Enter') {
                document.removeEventListener('keydown', this.checkHotKeys);
                this.setMarkIsConfirmed(true);
            }
        },
        setPoint(e, which) {
            if (e.touches) {
                this[which].x = e.changedTouches[0].clientX;
                this[which].y = e.changedTouches[0].clientY;
            } else {
                this[which].x = e.pageX;
                this[which].y = e.pageY;
            }
        },
        setCropBounds() {
            const { x, y } = this.startPosition();
            const { x: x1, y: y1 } = this.endPosition();
            // x boundaries
            if (x1 > this.getMainImgZoomWidth) {
                this.crop.width = this.getMainImgZoomWidth - x;
            } else if (x1 <= 0) {
                this.crop.width = x;
            } else {
                this.crop.width = Math.abs(x - x1);
            }
            // y boundaries
            if (y1 > this.getMainImgZoomHeight) {
                this.crop.height = this.getMainImgZoomHeight - y;
            } else if (y1 <= 0) {
                this.crop.height = y;
            } else {
                this.crop.height = Math.abs(y - y1);
            }
        },
        toggleEvents(val) {
            [this.container, document].forEach((element) => {
                ['mousemove', 'touchmove'].forEach((event) => {
                    if (!val) {
                        element.removeEventListener(event, this.onMouseMove);
                    } else {
                        element.addEventListener(event, this.onMouseMove);
                    }
                });
                ['mouseup', 'touchend'].forEach((event) => {
                    if (!val) {
                        element.removeEventListener(event, this.onMouseUp);
                    } else {
                        element.addEventListener(event, this.onMouseUp);
                    }
                });
            });
        },
        hideMenuPanel() {
            if (this.isMenuPanelVisible) { this.$store.commit('SET_MENU_PANEL_VISIBILITY', false); }
        },
        fitLandScapeWidth() {
            const newZoom = (this.viewerWidth / this.getMainImageParams.height).toFixed(2);
            if (this.getZoom !== newZoom) {
                this.$store.commit('SET_NEW_ZOOM', newZoom);
            }
        },
        setRotateOrigin(angle) {
            const reset = () => { this.origin = { x: 50, y: 50 }; };
            if (!this.isVerticalCenter) {
                if (angle === -90 || angle === 270) {
                    this.origin.y = 50 + (this.getMainImgZoomWidth / this.getMainImgZoomHeight - 1) * 100 / 2;
                } else if (angle === 90 || angle === -270) {
                    this.origin.x = 50 + (this.getMainImgZoomHeight / this.getMainImgZoomWidth - 1) * 100 / 2;
                } else {
                    reset();
                }
            } else {
                reset();
            }
        },
        containerSizeMath() {
            if (!this.isDesktop) {
                if (this.isMainImgLandscape) {
                    this.fitLandScapeWidth();
                } else {
                    this.$documentView.setFullWidth();
                }
            }
        },
        async setImage(route) {
            const imgData = await this.fetchImage(route, this);
            if (imgData) {
                const { width, height } = await this.createImage(imgData, this);
                if (width !== this.getMainImageParams.width || height !== this.getMainImageParams.height) {
                    this.$store.commit('SET_IMAGE_NATURAL_PARAMS', {
                        width,
                        height
                    });
                }
                return true;
            }
            return false;
        },
        async fetchImage(route) {
            const { page, negative = 0, rotate = 0 } = route.query;
            const { id, lang } = route.params;
            this.$i18n.locale = lang;
            this.imgIsLoading = true;
            // отмена предыдущего получения изображения при повторном вызове
            if (this.cancelFetchImage !== null) {
                this.cancelFetchImage();
            }
            const url = route.query.searchTask
                ? `/api/viewer/pdf?docId=${id}&search=${route.query.searchTask}&page=${page}`
                : `/api/viewer/pdf?docId=${id}&page=${page}&negative=${negative}&rotate=${rotate}`;
            try {
                const response = await api.get(
                    `${url}`, {
                        responseType: 'arraybuffer'
                    }
                );
                return response.data;
            } catch (err) {
                if (err.message) {
                    throw err;
                }
            }
            return false;
        },
        createImage(imgData) {
            return new Promise((resolve, reject) => {
                const img = new Image();
                this.imgUrl = `data:image/jpeg;base64,${getBufferFrom(imgData)}`;
                img.src = this.imgUrl;
                img.onerror = () => reject(new Error(`load ${this.imgUrl} fail`));
                img.onload = () => resolve(img);
            });
        }
    }
};
</script>

<style lang="sass" src="@/media/sass/components/document-views/document-view-img.sass"></style>
